/* UGC video row
   - Videos load and play (muted, looping) only while on screen and pause when scrolled away.
   - Each video has pause/play and sound buttons; turning one video's sound on mutes the others.
   - Visitors who prefer reduced motion see the cover until they press play.
   - Desktop arrows scroll the row when there are more cards than fit.
   - The button scrolls smoothly to the Bucket PDP hero (or a chosen section ID). */
(function () {
  'use strict';
  var reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
  var allVideos = [];

  function setupVideo(video) {
    if (video.dataset.ugcReady) return;
    video.dataset.ugcReady = '1';
    allVideos.push(video);
    var box = video.parentNode.querySelector('[data-ugc-controls]');
    var playBtn = box && box.querySelector('[data-ugc-play]');
    var soundBtn = box && box.querySelector('[data-ugc-sound]');
    var userPaused = reduce;

    function sync() {
      if (!box) return;
      box.hidden = false;
      playBtn.classList.toggle('is-paused', video.paused);
      playBtn.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
      soundBtn.classList.toggle('is-on', !video.muted);
      soundBtn.setAttribute('aria-pressed', String(!video.muted));
      soundBtn.setAttribute('aria-label', video.muted ? 'Turn sound on' : 'Turn sound off');
    }
    function play() {
      var p = video.play();
      if (p && p.catch) p.catch(sync);
    }

    ['play', 'pause', 'volumechange'].forEach(function (ev) { video.addEventListener(ev, sync); });

    if (playBtn) {
      playBtn.addEventListener('click', function () {
        userPaused = !video.paused;
        if (userPaused) video.pause(); else play();
      });
    }
    if (soundBtn) {
      soundBtn.addEventListener('click', function () {
        var turnOn = video.muted;
        if (turnOn) {
          allVideos.forEach(function (v) { if (v !== video) v.muted = true; });
          video.muted = false;
          userPaused = false;
          if (video.paused) play();
        } else {
          video.muted = true;
        }
      });
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        var on = entries[0].isIntersecting;
        if (on && !userPaused) play();
        else if (!on && !video.paused) video.pause();
      }, { threshold: 0.5 }).observe(video);
    } else if (!userPaused) {
      play();
    }
    sync();
  }

  function setupArrows(root) {
    var track = root.querySelector('[data-ugc-track]');
    var prev = root.querySelector('[data-ugc-prev]');
    var next = root.querySelector('[data-ugc-next]');
    if (!track || !prev || !next || track.dataset.ugcReady) return;
    track.dataset.ugcReady = '1';

    function update() {
      var overflow = track.scrollWidth - track.clientWidth > 2;
      prev.hidden = next.hidden = !overflow;
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
    }
    function step(dir) {
      var card = track.querySelector('li');
      var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      track.scrollBy({ left: dir * ((card ? card.offsetWidth : 200) + gap) });
    }
    prev.addEventListener('click', function () { step(-1); });
    next.addEventListener('click', function () { step(1); });
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  function setupButton(root) {
    var link = root.querySelector('[data-ugc-scroll]');
    if (!link || link.dataset.ugcReady) return;
    link.dataset.ugcReady = '1';
    link.addEventListener('click', function (e) {
      var id = link.getAttribute('data-ugc-scroll');
      var target = (id && document.getElementById(id)) || document.querySelector('[data-bph]');
      if (!target) return; // no hero on this page: let the link behave normally
      e.preventDefault();
      target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    });
  }

  /* Instagram cards: the card shows a scaled, non-clickable preview of Instagram's embed;
     tapping it opens the post's player in a full-screen dialog. Closing removes the iframe so the video stops. */
  var IG_WIDTH = 326; // Instagram's embed is laid out for this width
  function scalePreviews(root) {
    Array.prototype.forEach.call(root.querySelectorAll('[data-ugc-ig-preview]'), function (frame) {
      var box = frame.parentElement;
      var k = box.clientWidth / IG_WIDTH;
      if (!k) return;
      frame.style.transform = 'scale(' + k + ')';
      frame.style.height = Math.ceil(box.clientHeight / k) + 'px';
    });
  }
  function setupInstagram(root) {
    var dialog = root.querySelector('[data-ugc-ig-dialog]');
    var holder = dialog && dialog.querySelector('[data-ugc-ig-frame]');
    var buttons = root.querySelectorAll('[data-ugc-ig]');
    if (!dialog || !buttons.length || dialog.dataset.ugcReady) return;
    dialog.dataset.ugcReady = '1';
    scalePreviews(root);
    if ('ResizeObserver' in window) new ResizeObserver(function () { scalePreviews(root); }).observe(root);
    else window.addEventListener('resize', function () { scalePreviews(root); });

    var opener = null;
    Array.prototype.forEach.call(buttons, function (btn) {
      btn.addEventListener('click', function () {
        opener = btn;
        Array.prototype.forEach.call(root.querySelectorAll('[data-ugc-video]'), function (v) { v.pause(); });
        var frame = document.createElement('iframe');
        frame.src = btn.getAttribute('data-ugc-ig');
        frame.title = btn.getAttribute('aria-label').replace(' full screen', '');
        frame.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture';
        frame.setAttribute('allowfullscreen', '');
        holder.textContent = '';
        holder.appendChild(frame);
        if (typeof dialog.showModal === 'function') dialog.showModal(); else dialog.setAttribute('open', '');
        document.documentElement.classList.add('ugc-ig-open');
      });
    });
    dialog.addEventListener('close', function () {
      holder.textContent = '';
      document.documentElement.classList.remove('ugc-ig-open');
      if (opener) opener.focus({ preventScroll: true });
    });
    // Tap outside the player to close
    dialog.addEventListener('click', function (e) { if (e.target === dialog) dialog.close(); });
  }

  function init(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-ugc]'), function (root) {
      Array.prototype.forEach.call(root.querySelectorAll('[data-ugc-video]'), setupVideo);
      setupArrows(root);
      setupButton(root);
      setupInstagram(root);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
